import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';
import { getStaticPageMeta } from '@/seo/meta';
import { aboutData } from '@/data/aboutContent';
import AccreditationStats from '@/components/sections/AccreditationStats';
import ContactCta from '@/components/sections/ContactCta';

type IconItem = { title: string; description: string; image: string };

export default function About() {
  const { t } = useTranslation();
  usePageMeta(getStaticPageMeta('/about'));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const intro = t('aboutIntro.paragraphs', { returnObjects: true }) as string[];
  const content = t('about.content', { returnObjects: true }) as string[];
  const facility = t('about.facility', { returnObjects: true }) as IconItem[];
  const visionList = t('about.vision.list', { returnObjects: true }) as IconItem[];

  return (
    <>
      {/* Page Header */}
      <div
        className="page-header parallaxie"
        style={{
          backgroundImage: 'linear-gradient(rgba(25,35,36,0.6), rgba(25,35,36,0.6)), url(/images/page-header-bg.jpg)',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
        }}
      >
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="page-header-box">
                <h1>{t('about.pageTitle')}</h1>
                <nav>
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item"><a href="/">{t('common.home')}</a></li>
                    <li className="breadcrumb-item active">{t('about.breadcrumb')}</li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* About Us Section */}
      <div className="about-us page-about-us">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="about-us-images">
                <div className="about-img-1">
                  <img src={aboutData.image1} alt={t('about.pageTitle')} />
                </div>
                <div className="about-img-2">
                  <img src={aboutData.image2} alt={t('about.pageTitle')} />
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="about-us-content">
                <span className="mgc-eyebrow">{t('aboutIntro.eyebrow')}</span>
                <h2 className="mgc-heading" style={{ marginBottom: 20 }}>{t('aboutIntro.title')}</h2>
                {intro.map((p, i) => (
                  <p className="mgc-lead" key={i} style={{ marginBottom: 14 }}>{p}</p>
                ))}
                <div className="about-us-content-body" style={{ marginTop: 18 }}>
                  <ul>
                    {content.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
                <div style={{ marginTop: 20, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                  <Link to="/services" className="btn-default btn-highlighted">{t('common.viewServices')}</Link>
                </div>
              </div>
            </div>

            <div className="col-lg-12" style={{ marginTop: 56 }}>
              <AccreditationStats />
            </div>

            <div className="col-lg-12">
              <div className="about-facility-list">
                {facility.map((item, i) => (
                  <div key={i} className="about-facility-item">
                    <div className="icon-box">
                      <img src={item.image} alt={item.title} />
                    </div>
                    <div className="about-facility-content">
                      <h3>{item.title}</h3>
                      <p>{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Vision / Commitment */}
      <div className="vision-mission">
        <div className="container">
          <div className="row section-row align-items-center">
            <div className="col-lg-6">
              <div className="section-title dark-section">
                <h3>{t('about.vision.subTitle')}</h3>
                <h2>{t('about.vision.title')}</h2>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="section-title-content dark-section">
                <p>{t('about.vision.description')}</p>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-12">
              <div className="vision-mission-box">
                {visionList.map((item, i) => (
                  <div key={i} className="vision-mission-item">
                    <div className="icon-box">
                      <img src={item.image} alt={item.title} />
                    </div>
                    <div className="vision-mission-content">
                      <h3>{item.title}</h3>
                      <p>{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Capability (retained "best selling") */}
      <div className="best-selling">
        <div className="container">
          <div className="row">
            <div className="col-lg-6">
              <div className="best-selling-content">
                <div className="best-selling-content-img">
                  <img src={aboutData.selling.image1} alt={t('about.selling.title')} />
                </div>
                <div className="section-title">
                  <h3>{t('about.selling.subTitle')}</h3>
                  <h2>{t('about.selling.title')}</h2>
                  <p>{t('about.selling.description')}</p>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="best-selling-iamge">
                <img src={aboutData.selling.image2} alt={t('about.selling.title')} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Our Clients */}
      <div className="our-clients">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="our-clients-box">
                <div className="section-title" style={{ textAlign: 'center', marginBottom: 50 }}>
                  <h3>{t('about.clients.title')}</h3>
                  <h2>{t('about.clients.subTitle')}</h2>
                  <p>{t('about.clients.description')}</p>
                </div>
                <div className="partner-marquee">
                  <div className="partner-marquee-track">
                    {[...aboutData.clients.list, ...aboutData.clients.list].map((item, i) => (
                      <div key={i} className="partner-marquee-item">
                        <img src={item.url} alt={item.alt} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ContactCta />
    </>
  );
}
